<template>
    <PageHeader :title="$t('inbox.title')" @back="goBack" />

    <div v-if="unreadCount > 0" class="px-4 pb-4">
        <DiagonalCutButton
            @click="markAllAsRead"
            :disabled="isUpdating"
            variant="primary"
            cut-position="left-top"
        >
            {{ isUpdating ? $t('inbox.marking_all_read') : $t('inbox.mark_all_read') }}
        </DiagonalCutButton>
    </div>

    <div class="flex-1 overflow-y-auto p-4">
        <div v-if="isLoading" class="py-12 text-center">
            <p class="text-lg text-zinc-400">{{ $t('inbox.loading') }}</p>
        </div>

        <div v-else>
            <div v-if="todayNotifications.length > 0">
                <p class="font-title text-sm font-extrabold text-white uppercase">{{ $t('inbox.today') }}</p>

                <div v-for="notification in todayNotifications" :key="notification.id" class="mt-4">
                    <FriendshipNotification
                        v-if="isFriendshipNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <MatchInviteNotification
                        v-else-if="isMatchInviteNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <MatchInviteAcceptedNotification
                        v-else-if="isMatchInviteAcceptedNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <MatchInviteDeclinedNotification
                        v-else-if="isMatchInviteDeclinedNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <RemovedFromMatchNotification
                        v-else-if="isRemovedFromMatchNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <div v-else class="text-titles text-sm">
                        {{ $t('inbox.unknown_notification_type', { type: notification.type }) }}
                    </div>
                </div>
            </div>

            <div
                v-if="olderNotifications.length > 0"
                :class="{ 'mt-8': todayNotifications.length > 0 }"
            >
                <p class="font-title text-sm font-extrabold text-white uppercase">
                    {{ $t('inbox.recent') }}
                </p>

                <div v-for="notification in olderNotifications" :key="notification.id" class="mt-4">
                    <FriendshipNotification
                        v-if="isFriendshipNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <MatchInviteNotification
                        v-else-if="isMatchInviteNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <MatchInviteAcceptedNotification
                        v-else-if="isMatchInviteAcceptedNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <MatchInviteDeclinedNotification
                        v-else-if="isMatchInviteDeclinedNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <RemovedFromMatchNotification
                        v-else-if="isRemovedFromMatchNotification(notification)"
                        :notification="notification"
                        @read="markAsRead"
                    />
                    <div v-else class="text-titles text-sm">
                        {{ $t('inbox.unknown_notification_type', { type: notification.type }) }}
                    </div>
                </div>
            </div>

            <div
                v-if="todayNotifications.length === 0 && olderNotifications.length === 0"
                class="py-12 text-center"
            >
                <p class="text-lg text-zinc-400">{{ $t('inbox.empty') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useMatchInviteStore } from '@/stores/matchInvite.js'
import { apiService } from '@/services/api.js'
import PageHeader from '@/components/PageHeader.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import FriendshipNotification from '@/components/notifications/FriendshipNotification.vue'
import MatchInviteNotification from '@/components/notifications/MatchInviteNotification.vue'
import MatchInviteAcceptedNotification from '@/components/notifications/MatchInviteAcceptedNotification.vue'
import MatchInviteDeclinedNotification from '@/components/notifications/MatchInviteDeclinedNotification.vue'
import RemovedFromMatchNotification from '@/components/notifications/RemovedFromMatchNotification.vue'

export default {
    name: 'InboxView',
    components: {
        DiagonalCutButton,
        FriendshipNotification,
        MatchInviteNotification,
        MatchInviteAcceptedNotification,
        MatchInviteDeclinedNotification,
        RemovedFromMatchNotification,
        PageHeader,
    },
    data() {
        return {
            isUpdating: false,
            isLoading: true,
        }
    },
    computed: {
        ...mapStores(useAuthStore, useMatchInviteStore),

        todayNotifications() {
            return this.authStore.todayNotifications
        },

        olderNotifications() {
            return this.authStore.olderNotifications
        },

        unreadCount() {
            return this.authStore.totalUnreadCount
        },
    },
    async mounted() {
        this.isLoading = true
        try {
            await this.authStore.verifyToken()
        } finally {
            this.isLoading = false
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        handleMatchInviteClick(notification) {
            this.matchInviteStore.showInvite(notification)
        },

        async markAsRead(notification) {
            try {
                await apiService.delete(`/api/v1/inbox/${notification.id}`)
            } catch (error) {
                console.error('Failed to remove notification from inbox:', error)
            }
        },

        async markAllAsRead() {
            if (this.unreadCount === 0) return

            this.isUpdating = true
            try {
                await apiService.post('/api/v1/inbox/mark-all-read')

                // Mark all notifications as read in the store
                this.authStore.markAllInboxNotificationsAsRead()

                console.log('All notifications marked as read')
            } catch (error) {
                console.error('Failed to mark all notifications as read:', error)
                this.$toast(
                    this.$t('general.error'),
                    5000,
                    this.$t('inbox.error_mark_read'),
                )
            } finally {
                this.isUpdating = false
            }
        },

        async acceptFriendRequest(notification) {
            try {
                const friendshipId = notification.friendship_id || notification.from_user?.id
                if (!friendshipId) {
                    throw new Error('No friendship or user ID found in notification')
                }
                await this.authStore.acceptFriendRequest(friendshipId)
                this.markAsRead(notification)
            } catch (error) {
                console.error('Failed to accept friend request:', error)
                this.$toast(
                    this.$t('general.error'),
                    5000,
                    this.$t('inbox.error_accept_request'),
                )
            }
        },

        async declineFriendRequest(notification) {
            try {
                const friendshipId = notification.friendship_id || notification.from_user?.id
                if (!friendshipId) {
                    throw new Error('No friendship or user ID found in notification')
                }
                await this.authStore.declineFriendRequest(friendshipId)
                this.markAsRead(notification)
            } catch (error) {
                console.error('Failed to decline friend request:', error)
                this.$toast(
                    this.$t('general.error'),
                    5000,
                    this.$t('inbox.error_decline_request'),
                )
            }
        },

        isFriendshipNotification(notification) {
            return [
                'App\\Notifications\\FriendRequestReceivedNotification',
                'App\\Notifications\\FriendRequestAcceptedNotification',
                'App\\Notifications\\FriendRequestDeclinedNotification',
                'App\\Notifications\\FriendshipRemovedNotification',
                'App\\Notifications\\FriendshipCancelledRequestNotification',
                'App\\Notifications\\FriendRequestSentNotification',
                'App\\Notifications\\FriendshipEstablishedNotification',
            ].includes(notification.type)
        },

        isMatchInviteNotification(notification) {
            return notification.type === 'App\\Notifications\\InviteToMatchNotification'
        },

        isMatchInviteAcceptedNotification(notification) {
            return notification.type === 'App\\Notifications\\MatchInviteAcceptedNotification'
        },

        isMatchInviteDeclinedNotification(notification) {
            return notification.type === 'App\\Notifications\\MatchInviteDeclinedNotification'
        },

        isRemovedFromMatchNotification(notification) {
            return notification.type === 'App\\Notifications\\RemovedFromMatchNotification'
        },
    },
}
</script>
